// src/components/ideaNiv/IdeaHistory/IdeaHistoryView.tsx
import { Box, Stack, Flex, Text, Timeline, Avatar, Group, Tooltip, createStyles, Anchor, Loader } from '@mantine/core';
import { IconX, IconLink, IconUnlink, IconPlus, IconCloudUpload } from '@tabler/icons';
import { MentionProvider } from '../MentionTextarea/MentionContext';
import { useIdeaDetail } from '../../hooks/ideaPage/useIdeaQueries';
import {
  ProductAreaHeaderIcon,
  StatusHeaderIcon,
  OwnerHeaderIcon,
  GitBranchIcon,
} from '../../icons/x-symbol-svgrepo-com';
import { formatDateTime } from '../../utils/dateFormats';
import { formatDate } from '../../utils';
import { ALLOWED_EVENTS_IN_HISTORY } from '../../types/constants';

interface IdeaHistoryProps {
  activityLogs: any[];
  idea: any;
  setIdeaHistoryView: (value: boolean) => void;
}


const getFieldIcon = (type: string) => {
  const defaultProps = {
    size: 14,
    stroke: 1.5,
    color: '#5C5CEB'
  };

  switch (type.toLowerCase()) {
  case 'ownername':
    return <OwnerHeaderIcon {...defaultProps} />;

  case 'status':
    return <StatusHeaderIcon {...defaultProps} />;

  case 'componentid':
    return <ProductAreaHeaderIcon {...defaultProps} />;

  }
};
// Add this constant for allowed fields
const ALLOWED_FIELDS = ['ownerName', 'componentId', 'status'];

const getReadableFieldName = (field: string) => {
  const fieldMap: { [key: string]: string } = {
    ownerName: 'Owner',
    componentId: 'Product Area',
    status: 'Status',
  };
  return fieldMap[field] || field;
};


const truncateToMaxLen = (text: string, maxLen: number) =>
  text.length <= maxLen ? text : `${text.slice(0, maxLen - 1)}…`;

const IdeaHistoryLink = ({
  ideaId,
  fallbackTitle = 'Idea',
}: {
  ideaId: string;
  fallbackTitle?: string;
}) => {
  const { data: linkedIdea, isLoading, isFetching } = useIdeaDetail(ideaId);
  const title = linkedIdea?.title;
  const showLoader = (isLoading || isFetching) && !title;
  const displayTitle = title
    ? truncateToMaxLen(title, 60)
    : fallbackTitle;

  return (
    <Anchor
      href={`/idea/${ideaId}`}
      target="_blank"
      rel="noopener noreferrer"
      size="sm"
      title={title && title.length > 60 ? title : undefined}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 280 }}
    >
      {showLoader ? (
        <>
          <Loader size={12} />
          <Text span size="sm" color="dimmed">
            Loading…
          </Text>
        </>
      ) : (
        <Text span size="sm" lineClamp={2}>
          {displayTitle}
        </Text>
      )}
    </Anchor>
  );
};

const subIdeaRowSx = {
  borderRadius: 2,
  padding: 3,
  backgroundColor: '#5C5CEB1A',
  lineHeight: 16,
} as const;

const SubIdeaHistoryRow = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Flex>
    <Flex
      w="24px"
      h="24px"
      m={3}
      bg="#E9ECEF"
      sx={{ borderRadius: 2, padding: 5, backgroundColor: '#5C5CEB1A', lineHeight: 16 }}
    >
      {icon}
    </Flex>
    <Flex w="100%" h="100%" m={3} bg="#E9ECEF" sx={subIdeaRowSx}>
      <Text
        size="sm"
        style={{
          fontFamily: 'Inter',
          fontSize: 12,
          marginBottom: 2,
          letterSpacing: 0,
          verticalAlign: 'middle',
        }}
      >
        {children}
      </Text>
    </Flex>
  </Flex>
);

const HistoryHeader = ({ onClose }: { onClose: () => void }) => (
  <Flex
    mt="24px"
    pb="16px" 
    px="35px"
    justify="space-between"
    align="center"
    sx={{ borderBottom: "1px solid #D8D8DB" }}
  >
    <Flex justify="space-between" w="100%" h={52} align="center">
      <Stack fz="20px" fw={600} h="20px" ta="center" justify="center">
        Idea History
      </Stack>
      <Flex
        mb={2}
        mr="8px"
        sx={{ cursor: "pointer" }}
        align="center"
        onClick={onClose}
      >
        <IconX size={14} color="#5C5CEB" />
        <Text ml="4px" color="#5C5CEB" fz={12} py={6}>
          Close
        </Text>
      </Flex>
    </Flex>
  </Flex>
);

const UserAvatar = ({ initial }: { initial: string }) => (
  <Avatar
    radius="xl"
    size={16}
    styles={(theme) => ({ 
      root: {
        backgroundColor: '#585C68',
        border: 'none',
        color: 'white',
      },
      placeholder: {
        color: 'white',
        fontSize: '10px',
        backgroundColor: '#585C68',
        fontWeight: 500
      }
    })}
  >
    {initial}
  </Avatar>
);

const ChangeItem = ({ field, value }: { field: string; value: any }) => (
  <Flex>
    <Flex
      w="24px"
      h="24px"
      m={3}
      bg="#E9ECEF"
      sx={{ borderRadius: 2, padding: 5, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
    >
      {getFieldIcon(field)}  
    </Flex>
    <Flex
      w="100%"
      h="100%"
      m={3}
      bg="#E9ECEF"
      sx={{ borderRadius: 2, padding: 3, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
    >
      <Text 
        size="sm" 
        style={{
          fontFamily: 'Inter',
          fontSize: 12,
          marginBottom: 2,
          letterSpacing: 0,
          verticalAlign: 'middle',
        }}
      >
        <Text span weight={100}>
          {getReadableFieldName(field)}
        </Text>
        {' '}was changed
        {' '}from{' '}
        <Text span weight={700} color="dark">
          "{value.from || 'none'}"
        </Text>
        {' '}to{' '}
        <Text span weight={700} color="dark">
          "{value.to || 'none'}"
        </Text>
      </Text>
    </Flex>
  </Flex>
);

const ProductConnectedItem = ({ payload }: { payload: any }) => (
  <Flex>
    <Flex
      w="24px"
      h="24px"
      m={3}
      bg="#E9ECEF"
      sx={{ borderRadius: 2, padding: 5, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
    >
      <IconLink size={14} stroke={1.5} color="#5C5CEB" />
    </Flex>
    <Flex
      w="100%"
      h="100%"
      m={3}
      bg="#E9ECEF"
      sx={{ borderRadius: 2, padding: 3, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
    >
      <Text 
        size="sm" 
        style={{
          fontFamily: 'Inter',
          fontSize: 12,
          marginBottom: 2,
          letterSpacing: 0,
          verticalAlign: 'middle',
        }}
      >
        <Text span weight={100}>
          Product item connected
        </Text>
        {payload?.integration && (
          <>
            {' '}from{' '}
            <Text span weight={700} color="dark">
              {payload.integration}
            </Text>
          </>
        )}
        {payload?.newStatus && (
          <>
            {' '}with status{' '}
            <Text span weight={700} color="dark">
              "{payload.newStatus}"
            </Text>
          </>
        )}
      </Text>
    </Flex>
  </Flex>
);

const ProductDisconnectedItem = ({ payload }: { payload: any }) => (
  <Flex>
    <Flex
      w="24px"
      h="24px"
      m={3}
      bg="#E9ECEF"
      sx={{ borderRadius: 2, padding: 5, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
    >
      <IconUnlink size={14} stroke={1.5} color="#5C5CEB" />
    </Flex>
    <Flex
      w="100%"
      h="100%"
      m={3}
      bg="#E9ECEF"
      sx={{ borderRadius: 2, padding: 3, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
    >
      <Text 
        size="sm" 
        style={{
          fontFamily: 'Inter',
          fontSize: 12,
          marginBottom: 2,
          letterSpacing: 0,
          verticalAlign: 'middle',
        }}
      >
        <Text span weight={100}>
          Product item disconnected
        </Text>
        {payload?.fromStatus && (
          <>
            {' '}(was{' '}
            <Text span weight={700} color="dark">
              "{payload.fromStatus}"
            </Text>
            )
          </>
        )}
      </Text>
    </Flex>
  </Flex>
);

const ProductItemCreatedItem = ({ payload }: { payload: any }) => (
  <Flex>
    <Flex
      w="24px"
      h="24px"
      m={3}
      bg="#E9ECEF"
      sx={{ borderRadius: 2, padding: 5, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
    >
      <IconPlus size={14} stroke={1.5} color="#5C5CEB" />
    </Flex>
    <Flex
      w="100%"
      h="100%"
      m={3}
      bg="#E9ECEF"
      sx={{ borderRadius: 2, padding: 3, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
    >
      <Text 
        size="sm" 
        style={{
          fontFamily: 'Inter',
          fontSize: 12,
          marginBottom: 2,
          letterSpacing: 0,
          verticalAlign: 'middle',
        }}
      >
        <Text span weight={100}>
          Product item created
        </Text>
        {payload?.integration && (
          <>
            {' '}in{' '}
            <Text span weight={700} color="dark">
              {payload.integration}
            </Text>
          </>
        )}
        {payload?.title && (
          <>
            {': '}
            <Text span weight={700} color="dark">
              "{payload.title}"
            </Text>
          </>
        )}
      </Text>
    </Flex>
  </Flex>
);

const SyncedToExternalItem = ({ payload }: { payload: any }) => {
  // Handle both direct payload and nested structures
  const syncedFields = payload?.syncedFields || payload?.context?.syncedFields || [];
  const integration = payload?.integration || payload?.context?.integration || 'external system';
  const action = payload?.action || payload?.context?.action;
  const hasFields = Array.isArray(syncedFields) && syncedFields.length > 0;
  
  // Debug: log payload structure (remove in production if needed)
  if (process.env.NODE_ENV === 'development' && payload) {
    console.log('[SyncedToExternalItem] Payload:', payload);
    console.log('[SyncedToExternalItem] SyncedFields:', syncedFields);
  }
  
  return (
    <Stack spacing={4}>
      <Flex>
        <Flex
          w="24px"
          h="24px"
          m={3}
          bg="#E9ECEF"
          sx={{ borderRadius: 2, padding: 5, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
        >
          <IconCloudUpload size={14} stroke={1.5} color="#5C5CEB" />
        </Flex>
        <Flex
          w="100%"
          h="100%"
          m={3}
          bg="#E9ECEF"
          sx={{ borderRadius: 2, padding: 3, backgroundColor: '#5C5CEB1A', lineHeight: 16}}
        >
          <Text 
            size="sm" 
            style={{
              fontFamily: 'Inter',
              fontSize: 12,
              marginBottom: 2,
              letterSpacing: 0,
              verticalAlign: 'middle',
            }}
          >
            <Text span weight={100}>
              Synced to{' '}
            </Text>
            <Text span weight={700} color="dark">
              {integration}
            </Text>
            {action && (
              <>
                {' '}({action})
              </>
            )}
          </Text>
        </Flex>
      </Flex>
      {hasFields ? (
        <Flex ml={30} mt={4}>
          <Flex
            w="100%"
            h="100%"
            m={3}
            bg="#E9ECEF"
            sx={{ borderRadius: 2, padding: 3, backgroundColor: '#F8F9FA', lineHeight: 16}}
          >
            <Text 
              size="sm" 
              style={{
                fontFamily: 'Inter',
                fontSize: 11,
                color: '#868E96',
                fontStyle: 'italic',
              }}
            >
              Fields synced: <Text span weight={600} style={{ fontStyle: 'normal', color: '#212529' }}>{syncedFields.join(', ')}</Text>
            </Text>
          </Flex>
        </Flex>
      ) : (
        // Show a message if payload exists but no fields (for debugging)
        payload && (
          <Flex ml={30} mt={4}>
            <Text 
              size="xs" 
              style={{
                fontFamily: 'Inter',
                fontSize: 10,
                color: '#ADB5BD',
                fontStyle: 'italic',
              }}
            >
              (No field details available)
            </Text>
          </Flex>
        )
      )}
    </Stack>
  );
};

const TimelineItemHeader = ({ user, timestamp }: { user: string; timestamp: string }) => (
  <Group spacing="xs">
    <UserAvatar initial={user?.charAt(0)} />
    <Text size={12} fw={800} styles={{
      root: {
        color: '#212529'
      }
    }}>
      {user || 'Unknown user'}
    </Text>
    <Tooltip
      label={formatDate(timestamp)}
      withArrow
      width={295}
    >
      <Text 
        size={12}
        c="dimmed"
        styles={{
          root: {
            color: '#868E96'
          }
        }}
      >
        • {formatDateTime(timestamp)}
      </Text>
    </Tooltip>
  </Group>
);

// Main component
const isIdeaMergedEvent = (eventType: string) =>
  eventType === 'idea_merged' || eventType === 'idea-merged';

/** Activity logs may store merge details under `changes` (API) or `payload` (raw DB shape). */
const getIdeaMergePayload = (log: any): Record<string, unknown> => {
  const p = log?.payload;
  const ch = log?.changes;
  const fromPayload = p && typeof p === 'object' && !Array.isArray(p) ? p : {};
  const fromChanges = ch && typeof ch === 'object' && !Array.isArray(ch) ? ch : {};
  return { ...fromPayload, ...fromChanges };
};

const ideaIdToString = (id: unknown): string | undefined => {
  if (id == null || id === '') return undefined;
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && id !== null && '$oid' in (id as Record<string, unknown>)) {
    return String((id as { $oid: string }).$oid);
  }
  return String(id);
};

/** Activity logs may store details under `changes` (API) or `payload` (raw DB shape). */
const getEventChanges = (log: any): Record<string, unknown> => {
  const ch = log?.changes;
  const p = log?.payload;
  const fromChanges = ch && typeof ch === 'object' && !Array.isArray(ch) ? ch : {};
  const fromPayload = p && typeof p === 'object' && !Array.isArray(p) ? p : {};
  return { ...fromPayload, ...fromChanges };
};

const resolveSubIdeaRef = (changes: Record<string, unknown>) => {
  const id = ideaIdToString(
    changes.ideaId ?? changes.subIdeaId ?? changes._id,
  );
  const title =
    (changes.ideaTitle as string | undefined) ??
    (changes.subIdeaTitle as string | undefined) ??
    (changes.title as string | undefined);
  return { id, title };
};

const resolveParentIdeaRef = (changes: Record<string, unknown>) => {
  const id = ideaIdToString(changes.parentIdeaId);
  const title = changes.parentIdeaTitle as string | undefined;
  return { id, title };
};

const subIdeaEventIcon = (
  <GitBranchIcon color="#5C5CEB" />
);


/**
 * Renders a single sub-idea timeline event.
 * @param log          - activity log entry
 * @param label        - human-readable event label (e.g. "Sub idea attached")
 * @param resolver     - fn that extracts { id, title } from log changes
 * @param fallbackTitle - label used in IdeaHistoryLink when title is absent
 * @param showTitleFallback - when true, shows the raw title string if id is missing (default true)
 */
function renderSubIdeaEvent(
  log: any,
  label: string,
  resolver: (changes: Record<string, unknown>) => { id: string | undefined; title: string | undefined },
  fallbackTitle: string,
  showTitleFallback = true
) {
  const changes = getEventChanges(log);
  const { id, title } = resolver(changes);
  return (
    <Timeline.Item
      key={log._id}
      title={<TimelineItemHeader user={log.createdBy || 'System'} timestamp={log.createdAt} />}
    >
      <Stack spacing={0} ml="-10px" p={4} w="100%">
        <SubIdeaHistoryRow icon={subIdeaEventIcon}>
          <Text span weight={100}>
            {label}
          </Text>
          {id ? (
            <>
              {': '}
              <IdeaHistoryLink
                ideaId={id}
                fallbackTitle={title || fallbackTitle}
              />
            </>
          ) : (showTitleFallback && title) ? (
            <>
              {': '}
              <Text span weight={700} color="dark">
                &ldquo;{truncateToMaxLen(title, 72)}&rdquo;
              </Text>
            </>
          ) : null}
        </SubIdeaHistoryRow>
      </Stack>
    </Timeline.Item>
  );
}

export const IdeaHistoryView = ({ activityLogs, idea, setIdeaHistoryView }: IdeaHistoryProps) => {
  const useStyles = createStyles(() => ({
    historyContainer: {
      '& [class*="Timeline-item"]::before': {
        display: 'none !important',
      },
      '& [class*="Timeline-itemBullet"]': {
        display: 'none !important',
      },
    }
  }));
  const { classes } = useStyles();

  // Older ideas may not have an idea_created log; show fallback creation entry so they don't lose creation history
  const hasIdeaCreatedLog = activityLogs.some((log) => log.eventType === 'idea_created');

  const timelineStyles = {
    item: {
      paddingLeft: 0,
    },
    itemBullet: {
      display: 'none',
    },
  };

  return (
    <Box 
      bg="#FDFEFF"
      h='auto' 
      w="100%"
      styles={(theme) => ({
        root: {
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': {
              background: '#a8a8a8',
            },
          }
        }
      })}
    >
      <MentionProvider>
        <Stack justify="space-between">
          <Box>
            <HistoryHeader onClose={() => setIdeaHistoryView(false)} />
            
            <Box p="md" style={{ 
              border: '1px solid #eee', 
              paddingLeft: 36, 
              paddingRight: 36, 
              width: '100%', 
              maxHeight: '741px', 
              height: '600px',
              overflowY: 'scroll'
            }}>
              <Timeline styles={timelineStyles} className={classes.historyContainer} lineWidth={0} bulletSize={0}>
                {activityLogs.map((log) => {
                  // Handle different event types
                  if (!ALLOWED_EVENTS_IN_HISTORY.includes(log.eventType)) {
                    return null;
                  }

                  // Handle idea_updated events
                  if (log.eventType === 'idea_updated') {
                    const changes = log.changes?.changes || {};
                    const filteredChanges = Object.entries(changes)
                      .filter(([field]) => ALLOWED_FIELDS.includes(field))
                      .filter(([_, value]: [string, any]) => 
                        value.from !== value.to && 
                        (value.from !== undefined || value.to !== undefined)
                      );

                    if (filteredChanges.length === 0) {
                      return null;
                    }

                    return (
                      <Timeline.Item
                        key={log._id}
                        title={<TimelineItemHeader user={log.createdBy} timestamp={log.createdAt} />}
                      >
                        <Stack spacing={0} ml="-10px" p={4} w="100%">
                          {filteredChanges.map(([field, value]) => (
                            <ChangeItem key={field} field={field} value={value} />
                          ))}
                        </Stack>
                      </Timeline.Item>
                    );
                  }

                  // Handle idea_merged / idea-merged (survivor vs archived idea each get their own log shape)
                  if (isIdeaMergedEvent(log.eventType)) {
                    const c = getIdeaMergePayload(log);
                    const roleRaw = c.role as string | undefined;
                    const role = roleRaw?.toLowerCase();

                    const archivedId = ideaIdToString(c.archivedIdeaId);
                    const archivedTitle =
                      (c.archivedIdeaTitle as string | undefined) || 'Merged idea';
                    const mergedIntoId = ideaIdToString(c.mergedIntoIdeaId);
                    const mergedIntoTitle =
                      (c.mergedIntoIdeaTitle as string | undefined) || 'Idea';

                    const isArchivedSide =
                      role === 'archived' ||
                      (!role && mergedIntoId && !archivedId);
                    const displayArchived = truncateToMaxLen(archivedTitle, 72);
                    const displayInto = truncateToMaxLen(mergedIntoTitle, 72);

                    return (
                      <Timeline.Item
                        key={log._id}
                        title={
                          <TimelineItemHeader
                            user={log.createdBy}
                            timestamp={log.createdAt || log.timestamp}
                          />
                        }
                      >
                        <Stack spacing={0} ml="-10px" p={4} w="100%">
                          <Text size="sm" color="dimmed">
                            {isArchivedSide ? (
                              <>
                                Merged into{' '}
                                {mergedIntoId ? (
                                  <Anchor
                                    href={`/idea/${mergedIntoId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="sm"
                                    title={mergedIntoTitle.length > 72 ? mergedIntoTitle : undefined}
                                  >
                                    {displayInto}
                                  </Anchor>
                                ) : (
                                  <Text span size="sm" fw={500}>
                                    {displayInto}
                                  </Text>
                                )}
                              </>
                            ) : (
                              <>
                                Merged{' '}
                                {archivedId ? (
                                  <Anchor
                                    href={`/idea/${archivedId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="sm"
                                    title={archivedTitle.length > 72 ? archivedTitle : undefined}
                                  >
                                    {displayArchived}
                                  </Anchor>
                                ) : (
                                  <Text span size="sm" fw={500}>
                                    {displayArchived}
                                  </Text>
                                )}
                                {' '}
                                into this idea
                              </>
                            )}
                          </Text>
                        </Stack>
                      </Timeline.Item>
                    );
                  }

                  // Handle product_connected events
                  if (log.eventType === 'product_connected') {
                    return (
                      <Timeline.Item
                        key={log._id}
                        title={<TimelineItemHeader user={log.createdBy} timestamp={log.createdAt} />}
                      >
                        <Stack spacing={0} ml="-10px" p={4} w="100%">
                          <ProductConnectedItem payload={log.changes} />
                        </Stack>
                      </Timeline.Item>
                    );
                  }

                  // Handle product_disconnected events
                  if (log.eventType === 'product_disconnected') {
                    return (
                      <Timeline.Item
                        key={log._id}
                        title={<TimelineItemHeader user={log.createdBy} timestamp={log.createdAt} />}
                      >
                        <Stack spacing={0} ml="-10px" p={4} w="100%">
                          <ProductDisconnectedItem payload={log.changes} />
                        </Stack>
                      </Timeline.Item>
                    );
                  }

                  // Handle product_item_created events
                  if (log.eventType === 'product_item_created') {
                    return (
                      <Timeline.Item
                        key={log._id}
                        title={<TimelineItemHeader user={log.createdBy} timestamp={log.createdAt} />}
                      >
                        <Stack spacing={0} ml="-10px" p={4} w="100%">
                          <ProductItemCreatedItem payload={log.changes} />
                        </Stack>
                      </Timeline.Item>
                    );
                  }

                  // Handle synced_to_external events
                  if (log.eventType === 'synced_to_external') {
                    return (
                      <Timeline.Item
                        key={log._id}
                        title={<TimelineItemHeader user={log.createdBy || 'System'} timestamp={log.createdAt} />}
                      >
                        <Stack spacing={0} ml="-10px" p={4} w="100%">
                          <SyncedToExternalItem payload={log.changes || {}} />
                        </Stack>
                      </Timeline.Item>
                    );
                  }

                  if (log.eventType === 'idea_created') {
                    const parentIdeaId = log.changes?.parentIdeaId;
                    return (
                      <Timeline.Item
                        key={log._id}
                        title={<TimelineItemHeader user={log.createdBy || 'System'} timestamp={log.createdAt} />}
                      >
                        <Stack spacing={0} ml="-10px" p={4} w="100%">
                          <Text size="sm" color="dimmed">
                            {log.changes?.ideaSource ? (
                              <> {log.changes?.ideaSource}</>
                            ) : (
                              <> Created the idea</>
                            )}
                            {parentIdeaId && (
                              <>
                                {'  '}
                                <IdeaHistoryLink
                                  ideaId={parentIdeaId}
                                  fallbackTitle="Parent idea"
                                />
                              </>
                            )}
                          </Text>
                        </Stack>
                      </Timeline.Item>
                    );
                  }

                  if (log.eventType === 'sub_idea_created') {
                    return renderSubIdeaEvent(log, 'Sub idea created', resolveSubIdeaRef, 'Sub idea', false);
                  }

                  if (log.eventType === 'sub_idea_attached') {
                    return renderSubIdeaEvent(log, 'Sub idea attached', resolveSubIdeaRef, 'Sub idea');
                  }

                  if (log.eventType === 'sub_idea_detached') {
                    return renderSubIdeaEvent(log, 'Sub idea detached', resolveSubIdeaRef, 'Sub idea');
                  }

                  if (log.eventType === 'sub_idea_linked_to_parent') {
                    return renderSubIdeaEvent(log, 'Linked to parent idea', resolveParentIdeaRef, 'Parent idea');
                  }

                  if (log.eventType === 'sub_idea_unlinked_from_parent') {
                    return renderSubIdeaEvent(log, 'Unlinked from parent idea', resolveParentIdeaRef, 'Parent idea');
                  }

                  return null;
                })}
                {!hasIdeaCreatedLog && idea?.createdAt && (
                  <Timeline.Item
                    key="idea-created-fallback"
                    title={
                      <TimelineItemHeader
                        user={idea?.owner?.name ?? idea?.ownerName ?? 'System'}
                        timestamp={idea.createdAt}
                      />
                    }
                  >
                    <Stack spacing={0} ml="-10px" p={4} w="100%">
                      <Text size="sm" color="dimmed">
                        Created the idea
                      </Text>
                    </Stack>
                  </Timeline.Item>
                )}
              </Timeline>
               
            </Box>
          </Box>
        </Stack>
      </MentionProvider>
    </Box>
  );
};